import { useRef } from "react";
import { useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "react-toastify";

export default function useFeedSocket(
  socket,
  {
    chatId,

    /* ================= POSTS ================= */
    onNewPost,
    onNewFollowingPost,

    /* ================= COMMENTS ================= */
    onNewComment,
    onNewCommentRepost,

    /* ================= COUNTS / UPDATES ================= */
    onLikeUpdate,
    onCommentLikeUpdate,
    onRepostCountUpdate,
    onCommentRepostCountUpdate,
    onViewUpdate,
    onCommentViewUpdate,

    /* ================= OPTIMISTIC ================= */
    onOptimisticPost,
    onOptimisticComment,

    /* ================= INTERACTIONS ================= */
    onInteractionCleared,
    onUnreadUpdate,

    /* ================= NOTIFICATIONS ================= */
    onUnreadLikeUpdate,
    onUnreadRepostUpdate,
    onUnreadCommentLikeUpdate,
    onUnreadCommentRepostUpdate,

    onLikeCleared,
    onRepostCleared,
    onCommentRepostCleared,

    /* ================= FOLLOW ================= */
    onFollowedYou,
    onUnfollowedYou,
    onFollowSuccess,
    onUnfollowSuccess,
    onFollowCounts,

    /* ================= SUPPORT ================= */
    onPostSupported,
    onCommentSupported,
    onSupportCountUpdate,
    onCommentSupportCountUpdate,
    onUnreadSupportPost,
    onUnreadSupportComment,
    onSupportPostCleared,
    onSupportCommentCleared,
    onSupportCleared,

    /* ================= ERRORS ================= */
    onError,
  } = {}
) {
  /* ----------------------------------------
     REFS — declared before any useCallback
     that references them to avoid hoisting bugs
  ---------------------------------------- */
  const optimisticPostsRef    = useRef(new Set());
  const optimisticCommentsRef = useRef(new Set());

  /* ----------------------------------------
     SOCKET LISTENERS
  ---------------------------------------- */
  useEffect(() => {
    if (!socket) return;

    /* ================= POSTS ================= */
    const handleGlobalPost = (post) => {
      onNewPost?.(post);
    };

    const handleFollowingPost = (post) => {
      onNewFollowingPost?.(post);
    };

    socket.on("feed-new-post-global",    handleGlobalPost);
    socket.on("feed-new-post-following", handleFollowingPost);

    /* ================= COMMENTS ================= */
    const handleNewComment = (payload) => {
      onNewComment?.({
        postId:                     payload.postId,
        comment:                    payload.comment,
        postCommentsCount:          payload.postCommentsCount,
        parentCommentCommentsCount: payload.parentCommentCommentsCount,
        parentCommentId:            payload.parentCommentId,
        removeOptimisticCommentId:  payload.removeOptimisticCommentId,
      });
    };

    const handleCommentCountUpdate = (payload) => {
      onNewComment?.({
        postId:                     payload.postId,
        comment:                    null,
        postCommentsCount:          payload.postCommentsCount,
        parentCommentCommentsCount: payload.parentCommentCommentsCount,
        parentCommentId:            payload.parentCommentId,
      });
    };

    socket.on("feed-new-comment",          handleNewComment);
    socket.on("feed-comment-count-update", handleCommentCountUpdate);

    /* ================= LIKES ================= */
    const handleLikeUpdate = (payload) => {
      onLikeUpdate?.(payload);
    };
    socket.on("feed-like-update", handleLikeUpdate);

    /* ================= COMMENT LIKES ================= */
    const handleCommentLikeUpdate = (payload) => {
      onCommentLikeUpdate?.(payload);
    };
    socket.on("feed-comment-like-update", handleCommentLikeUpdate);

    /* ================= COMMENT REPOST COUNT ================= */
    const handleCommentRepostCountUpdate = (payload) => {
      onCommentRepostCountUpdate?.(payload);
    };
    socket.on("feed-comment-repost-count-update", handleCommentRepostCountUpdate);

    /* ================= REPOST COUNT ================= */
    const handleRepostCountUpdate = (payload) => {
      onRepostCountUpdate?.(payload);
    };
    socket.on("feed-repost-count-update", handleRepostCountUpdate);

    /* ================= VIEW COUNT (POST) ================= */
    const handleViewUpdate = (payload) => {
      onViewUpdate?.(payload);
    };
    socket.on("feed-view-update", handleViewUpdate);

    /* ================= VIEW COUNT (COMMENT) ================= */
    const handleCommentViewUpdate = (payload) => {
      onCommentViewUpdate?.(payload);
    };
    socket.on("feed-comment-view-update", handleCommentViewUpdate);

    /* ================= FOLLOW EVENTS ================= */
    const handleFollowedYou     = (p) => onFollowedYou?.(p);
    const handleUnfollowedYou   = (p) => onUnfollowedYou?.(p);
    const handleFollowSuccess   = (p) => onFollowSuccess?.(p);
    const handleUnfollowSuccess = (p) => onUnfollowSuccess?.(p);
    const handleFollowCounts    = (p) => onFollowCounts?.(p);

    socket.on("feed-followed-you",     handleFollowedYou);
    socket.on("feed-unfollowed-you",   handleUnfollowedYou);
    socket.on("feed-follow-success",   handleFollowSuccess);
    socket.on("feed-unfollow-success", handleUnfollowSuccess);
    socket.on("feed-follow-counts",    handleFollowCounts);

    /* ================= SUPPORT RECEIVED ================= */
    const handlePostSupported = (payload) => {
      onPostSupported?.({
        ...payload,
        actorChatId:
          payload.actorChatId ||
          payload.senderChatId,
      });
    };

    const handleCommentSupported = (payload) => {
      onCommentSupported?.({
        ...payload,
        actorChatId:
          payload.actorChatId ||
          payload.senderChatId,
      });
    };

    socket.on("feed-post-supported",    handlePostSupported);
    socket.on("feed-comment-supported", handleCommentSupported);

    /* ================= SUPPORT COUNT UPDATES (ALL USERS) ================= */
    const handleSupportCountUpdate = (payload) => {
      onSupportCountUpdate?.(payload);
    };
    socket.on("feed-support-count-update", handleSupportCountUpdate);

    const handleCommentSupportCountUpdate = (payload) => {
      onCommentSupportCountUpdate?.(payload);
    };
    socket.on("feed-comment-support-count-update", handleCommentSupportCountUpdate);

    /* ================= UNREAD SUPPORT — POST ================= */
    const handleUnreadSupportPost = (payload) => {
      const { postId } = payload || {};

      if (!postId) return;

      onUnreadSupportPost?.(payload);
    };

    socket.on("feed-unread-support-post", handleUnreadSupportPost);

    /* ================= UNREAD SUPPORT — COMMENT ================= */
    const handleUnreadSupportComment = (payload) => {
      const { commentId } = payload || {};

      if (!commentId) return;

      onUnreadSupportComment?.(payload);
    };

    socket.on("feed-unread-support-comment", handleUnreadSupportComment);

    /* ================= CLEAR SUPPORT NOTIFICATIONS ================= */
    const handleSupportPostCleared = ({ postId, isRead = true }) => {
      onSupportPostCleared?.({ postId, isRead });
    };

    const handleSupportCommentCleared = ({ commentId, isRead = true }) => {
      onSupportCommentCleared?.({ commentId, isRead });
    };

    socket.on("feed-support-post-cleared",    handleSupportPostCleared);
    socket.on("feed-support-comment-cleared", handleSupportCommentCleared);

    /* ================= ERRORS ================= */
    const handleFeedError = (err) => {
      optimisticPostsRef.current.forEach((postId) => {
        onNewPost?.((prev) =>
          prev?.filter?.((p) => String(p.postId) !== String(postId))
        );
        onNewFollowingPost?.((prev) =>
          prev?.filter?.((p) => String(p.postId) !== String(postId))
        );
      });
      optimisticPostsRef.current.clear();

      optimisticCommentsRef.current.forEach((commentId) => {
        onNewComment?.({ removeOptimisticCommentId: commentId });
      });
      optimisticCommentsRef.current.clear();

      onError?.(err);
    };

    socket.on("feed-error", handleFeedError);

    /* ================= UNREAD UPDATE ================= */
    const handleUnreadUpdate = ({ postId, unreadCount, activityAt, postPreview, actorChatId }) => {
      if (!postId) return;

      onUnreadUpdate?.(postId, unreadCount, activityAt, postPreview, actorChatId);
    };

    socket.on("feed-unread-update", handleUnreadUpdate);

    /* ================= UNREAD LIKE UPDATE ================= */
    const handleUnreadLikeUpdate = ({
      postId, unreadCount, activityAt,
      actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName,
      previewText,
    }) => {
      if (!postId) return;
      onUnreadLikeUpdate?.({ postId, unreadCount, activityAt, actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName, previewText });
    };

    socket.on("feed-unread-like-update", handleUnreadLikeUpdate);

    /* ================= UNREAD REPOST UPDATE ================= */
    const handleUnreadRepostUpdate = ({
      postId, unreadCount, activityAt,
      actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName,
      previewText, isQuote,
    }) => {
      if (!postId) return;
      onUnreadRepostUpdate?.({ postId, unreadCount, activityAt, actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName, previewText, isQuote });
    };

    socket.on("feed-unread-repost-update", handleUnreadRepostUpdate);

    /* ================= UNREAD COMMENT LIKE UPDATE ================= */
    const handleUnreadCommentLikeUpdate = ({
      commentId, postId, unreadCount, activityAt,
      actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName,
      previewText, cleared = false,
    }) => {
      if (!commentId) return;
      onUnreadCommentLikeUpdate?.({ commentId, postId, unreadCount, activityAt, actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName, previewText, cleared });
    };

    socket.on("feed-unread-comment-like-update", handleUnreadCommentLikeUpdate);

    /* ================= UNREAD COMMENT REPOST UPDATE ================= */
    const handleUnreadCommentRepostUpdate = ({
      commentId, unreadCount, activityAt,
      actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName,
      postId, previewText, isQuote,
    }) => {
      if (!commentId) return;
      onUnreadCommentRepostUpdate?.({ commentId, unreadCount, activityAt, actorChatId, actorUsername, actorProfileImageUrl, actorDisplayName, postId, previewText, isQuote });
    };

    socket.on("feed-unread-comment-repost-update", handleUnreadCommentRepostUpdate);

    /* ================= CLEAR SINGLE INTERACTION ================= */
    const handleInteractionCleared = ({ postId, chatId: clearedChatId }) => {
      if (!postId) return;
      if (chatId && clearedChatId && String(chatId) !== String(clearedChatId)) return;

      onInteractionCleared?.(postId);
    };

    socket.on("feed-interaction-cleared", handleInteractionCleared);
const handleAllNotificationsCleared = () => {
  onLikeCleared?.("__ALL__");
  onRepostCleared?.("__ALL__");
  onUnreadLikeUpdate?.({ postId: "__ALL__", unreadCount: 0 });
  onUnreadRepostUpdate?.({ postId: "__ALL__", unreadCount: 0 });
  onUnreadCommentLikeUpdate?.({ commentId: "__ALL__", unreadCount: 0 });
  onUnreadCommentRepostUpdate?.({ commentId: "__ALL__", unreadCount: 0 });

  onSupportCleared?.("__ALL__");
};
    
    socket.on("feed-all-notifications-cleared", handleAllNotificationsCleared);

    /* ================= CLEANUP ================= */
    return () => {
      socket.off("feed-new-post-global",    handleGlobalPost);
      socket.off("feed-new-post-following", handleFollowingPost);

      socket.off("feed-new-comment",          handleNewComment);
      socket.off("feed-comment-count-update", handleCommentCountUpdate);

      socket.off("feed-like-update",                 handleLikeUpdate);
      socket.off("feed-comment-like-update",         handleCommentLikeUpdate);
      socket.off("feed-comment-repost-count-update", handleCommentRepostCountUpdate);
      socket.off("feed-repost-count-update",         handleRepostCountUpdate);
      socket.off("feed-view-update",                 handleViewUpdate);
      socket.off("feed-comment-view-update",         handleCommentViewUpdate);

      socket.off("feed-followed-you",     handleFollowedYou);
      socket.off("feed-unfollowed-you",   handleUnfollowedYou);
      socket.off("feed-follow-success",   handleFollowSuccess);
      socket.off("feed-unfollow-success", handleUnfollowSuccess);
      socket.off("feed-follow-counts",    handleFollowCounts);

      socket.off("feed-post-supported",    handlePostSupported);
      socket.off("feed-comment-supported", handleCommentSupported);

      socket.off("feed-support-count-update",         handleSupportCountUpdate);
      socket.off("feed-comment-support-count-update", handleCommentSupportCountUpdate);
      socket.off("feed-unread-support-post",          handleUnreadSupportPost);
      socket.off("feed-unread-support-comment",       handleUnreadSupportComment);
      socket.off("feed-support-post-cleared",         handleSupportPostCleared);
      socket.off("feed-support-comment-cleared",      handleSupportCommentCleared);

      socket.off("feed-error",                        handleFeedError);
      socket.off("feed-unread-update",                handleUnreadUpdate);
      socket.off("feed-unread-like-update",           handleUnreadLikeUpdate);
      socket.off("feed-unread-repost-update",         handleUnreadRepostUpdate);
      socket.off("feed-unread-comment-like-update",   handleUnreadCommentLikeUpdate);
      socket.off("feed-unread-comment-repost-update", handleUnreadCommentRepostUpdate);
      socket.off("feed-interaction-cleared",          handleInteractionCleared);
      socket.off("feed-all-notifications-cleared",    handleAllNotificationsCleared);
    };
  }, [
    socket,
    chatId,
    onNewPost,
    onNewFollowingPost,
    onNewComment,
    onNewCommentRepost,
    onLikeUpdate,
    onCommentLikeUpdate,
    onRepostCountUpdate,
    onCommentRepostCountUpdate,
    onViewUpdate,
    onCommentViewUpdate,
    onInteractionCleared,
    onUnreadUpdate,
    onUnreadLikeUpdate,
    onUnreadRepostUpdate,
    onUnreadCommentLikeUpdate,
    onUnreadCommentRepostUpdate,
    onLikeCleared,
    onRepostCleared,
    onCommentRepostCleared,
    onFollowedYou,
    onUnfollowedYou,
    onFollowSuccess,
    onUnfollowSuccess,
    onFollowCounts,
    onPostSupported,
    onCommentSupported,
    onSupportCountUpdate,
    onCommentSupportCountUpdate,
    onUnreadSupportPost,
    onUnreadSupportComment,
    onSupportPostCleared,
    onSupportCommentCleared,
    onSupportCleared,
    onError,
  ]);

  /* ----------------------------------------
     CREATE POST
  ---------------------------------------- */
  const createPost = useCallback(
  ({ chatId, authorUsername, authorDisplayName = null, text = null, media = [], reply = null }) => {
    if (!socket || !chatId || !authorUsername) return;

        const postId = uuidv4();

    const optimisticPost = {
      postId,
      isOptimistic: true,
      author: {
        chatId,
        username:    authorUsername?.replace(/^@/, "") || "",
        displayName: authorDisplayName || null,
      },
      text,
      media,
      reply,
      likesCount:    0,
      commentsCount: 0,
      repostsCount:  0,
      viewsCount:    0,
      reportsCount:  0,
      likedByMe:     false,
      createdAt:     new Date(),
    };

    optimisticPostsRef.current.add(postId);

    onOptimisticPost?.(optimisticPost);
    onNewFollowingPost?.(optimisticPost);

    socket.emit("feed-create-post", { postId, chatId, authorUsername, text, media, reply });

    toast.success("Posted!");   // ✅ NEW — visible confirmation regardless of feed/scroll state
  },
  [socket, onOptimisticPost, onNewFollowingPost]
);
  /* ----------------------------------------
     VIEW POST / COMMENT
  ---------------------------------------- */
  const viewPost = useCallback(
    ({ postId = null, commentId = null }) => {
      if (!socket) return;
      if (!postId && !commentId) return;
      socket.emit("feed:view", { postId, commentId });
    },
    [socket]
  );

  /* ----------------------------------------
     LIKE POST
  ---------------------------------------- */
  const likePost = useCallback(
    ({ postId, chatId }) => {
      if (!socket || !postId || !chatId) return;
      socket.emit("feed-like-post", { postId, chatId });
    },
    [socket]
  );

  /* ----------------------------------------
     LIKE COMMENT
  ---------------------------------------- */
  const likeComment = useCallback(
    ({ commentId, chatId }) => {
      if (!socket || !commentId || !chatId) return;
      socket.emit("feed-like-comment", { commentId, chatId });
    },
    [socket]
  );

  /* ----------------------------------------
     REPOST POST
  ---------------------------------------- */
  const repostPost = useCallback(
  ({ postId, chatId, originalPost, text = null, authorUsername = null, authorDisplayName = null }) => {
    if (!socket || !postId || !chatId || !originalPost) return;

    const repostId = uuidv4();
    const isQuote  = !!text?.trim();

    const optimisticRepost = {
      postId:         repostId,
      isOptimistic:   true,
      isRepost:       true,
      isQuote,
      repostOfPostId: originalPost.postId,
      repostAuthorId: chatId,
      author: {
        chatId,
        // ✅ FIXED — this is the person reposting, not the original author
        username:    authorUsername?.replace(/^@/, "") || originalPost.author?.username,
        displayName: authorDisplayName || null,
      },
      originalPost,
      text:          isQuote ? text : null,
      media:         [],
      likesCount:    0,
      commentsCount: 0,
      repostsCount:  0,
      viewsCount:    0,
      reportsCount:  0,
      likedByMe:     false,
      createdAt:     new Date(),
    };

    optimisticPostsRef.current.add(repostId);

    onOptimisticPost?.(optimisticRepost);
    onNewFollowingPost?.(optimisticRepost);

    socket.emit("feed-repost", { repostId, postId, chatId, text });

    toast.success(isQuote ? "Quoted!" : "Reposted!");   // ✅ NEW
  },
  [socket, onOptimisticPost, onNewFollowingPost]
);

  /* ----------------------------------------
     REPOST COMMENT
  ---------------------------------------- */
  const repostComment = useCallback(
  ({ commentId, chatId, originalComment, text = null, authorUsername = null, authorDisplayName = null }) => {
    if (!socket || !commentId || !chatId || !originalComment) return;

    const repostId = uuidv4();
    const isQuote  = !!text?.trim();

    const optimisticFeedItem = {
      postId:            repostId,
      isOptimistic:      true,
      isRepost:          true,
      isQuote,
      isCommentRepost:   true,
      repostOfCommentId: originalComment.commentId,
      author: {
        chatId,
        // ✅ FIXED — this is the person reposting, not the original comment's author
        username:    authorUsername?.replace(/^@/, "") || originalComment.author?.username,
        displayName: authorDisplayName || null,
      },
      originalComment,
      text:          isQuote ? text : null,
      likesCount:    0,
      commentsCount: 0,
      repostsCount:  0,
      viewsCount:    0,
      reportsCount:  0,
      likedByMe:     false,
      createdAt:     new Date(),
    };

    optimisticPostsRef.current.add(repostId);

    onOptimisticPost?.(optimisticFeedItem);
    onNewFollowingPost?.(optimisticFeedItem);

    socket.emit("feed-repost-comment", { repostId, commentId, chatId, text });

    toast.success(isQuote ? "Quoted!" : "Reposted!");   // ✅ NEW
  },
  [socket, onOptimisticPost, onNewFollowingPost]
);
  /* ----------------------------------------
     ADD COMMENT
  ---------------------------------------- */
  const addComment = useCallback(
    ({
      postId,
      chatId,
      authorUsername,
      authorDisplayName = null,
      text,
      parentCommentId = null,
      quote           = null,
      media           = [],
    }) => {
      if (!socket || !postId || !chatId) return;
      if (!text?.trim() && (!Array.isArray(media) || media.length === 0)) return;

      const commentId = uuidv4();

      const optimisticComment = {
        commentId,
        isOptimistic:   true,
        postId,
        parentCommentId,
        author: {
          chatId,
          username:    authorUsername?.replace(/^@/, "") || "",
          displayName: authorDisplayName || null,
        },
        text:          text ? text.trim() : "",
        media,
        quote,
        likesCount:    0,
        repostsCount:  0,
        commentsCount: 0,
        viewsCount:    0,
        reportsCount:  0,
        likedByMe:     false,
        isEdited:      false,
        isDeleted:     false,
        createdAt:     new Date(),
      };

          optimisticCommentsRef.current.add(commentId);

    onOptimisticComment?.({ postId, comment: optimisticComment, parentCommentId });

    socket.emit("feed-add-comment", {
      commentId, postId, chatId, text, parentCommentId, quote, media,
    });

    toast.success(parentCommentId ? "Reply sent!" : "Comment posted!");   // ✅ NEW
  },
  [socket, onOptimisticComment]
);

  /* ----------------------------------------
     CLEAR SINGLE INTERACTION UNREAD
  ---------------------------------------- */
  const clearInteractionUnread = useCallback(
    ({ postId, chatId }) => {
      if (!socket || !postId || !chatId) return;
      socket.emit("feed-clear-interaction", { postId, chatId });
    },
    [socket]
  );

  /* ----------------------------------------
     CLEAR ALL NOTIFICATIONS
  ---------------------------------------- */
  const clearAllNotifications = useCallback(() => {
    if (!socket) return;
    socket.emit("feed-clear-all-notifications");
  }, [socket]);

  /* ----------------------------------------
     FOLLOW / UNFOLLOW
  ---------------------------------------- */
  const followUser = useCallback(
    ({ targetChatId }) => {
      if (!socket || !targetChatId) return;
      socket.emit("feed-follow-user", { targetChatId });
    },
    [socket]
  );

  const unfollowUser = useCallback(
    ({ targetChatId }) => {
      if (!socket || !targetChatId) return;
      socket.emit("feed-unfollow-user", { targetChatId });
    },
    [socket]
  );

  const getFollowCounts = useCallback(
    ({ targetChatId }) => {
      if (!socket || !targetChatId) return;
      socket.emit("feed-get-follow-counts", { targetChatId });
    },
    [socket]
  );

  /* ----------------------------------------
     SUPPORT POST (USWD TIP)
  ---------------------------------------- */
 const supportPost = useCallback(
  ({ postId, amount, timestamp }) => {
    if (!socket || !postId || !amount || !timestamp) return;

    socket.emit("feed-support-post", {
      postId,
      amount,
      timestamp,
    });
  },
  [socket]
);

  /* ----------------------------------------
     SUPPORT COMMENT (USWD TIP)
  ---------------------------------------- */
 const supportComment = useCallback(
  ({ commentId, amount, timestamp }) => {
    if (!socket || !commentId || !amount || !timestamp) return;

    socket.emit("feed-support-comment", {
      commentId,
      amount,
      timestamp,
    });
  },
  [socket]
);

  /* ----------------------------------------
     CLEAR SUPPORT POST NOTIFICATION
  ---------------------------------------- */
  const clearSupportPost = useCallback(
    ({ postId }) => {
      if (!socket || !postId) return;
      socket.emit("feed-clear-support-post", { postId });
    },
    [socket]
  );

  /* ----------------------------------------
     CLEAR SUPPORT COMMENT NOTIFICATION
  ---------------------------------------- */
  const clearSupportComment = useCallback(
    ({ commentId }) => {
      if (!socket || !commentId) return;
      socket.emit("feed-clear-support-comment", { commentId });
    },
    [socket]
  );

  /* ----------------------------------------
     SUPPORT RESULT LISTENERS (one-off, opt-in)
     These are request/response events scoped
     to the sending socket only — exposed as
     subscription helpers rather than always-on
     listeners in the main effect above.
  ---------------------------------------- */
  const onSupportPostResult = useCallback(
    (cb) => {
      if (!socket) return () => {};
      socket.on("feed-support-post-result", cb);
      return () => socket.off("feed-support-post-result", cb);
    },
    [socket]
  );

  const onSupportCommentResult = useCallback(
    (cb) => {
      if (!socket) return () => {};
      socket.on("feed-support-comment-result", cb);
      return () => socket.off("feed-support-comment-result", cb);
    },
    [socket]
  );

  return {
    createPost,
    likePost,
    likeComment,
    viewPost,
    addComment,
    repostPost,
    repostComment,
    followUser,
    unfollowUser,
    getFollowCounts,
    clearInteractionUnread,
    clearAllNotifications,
    supportPost,
    supportComment,
    clearSupportPost,
    clearSupportComment,
    onSupportPostResult,
    onSupportCommentResult,
  };
}